import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Flex, getStylesRef, Group, Input, Stack, Tooltip, UnstyledButton} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {ReactNode} from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

const ACTION_ICON_BUTTON_SIZE = '20px';
const ACTION_ICON_GLYPH_SIZE = '12px';

export function hasFieldError(error?: string | null): boolean {
  return error != null && error !== '';
}

/**
 * `aria-labelledby` for non-input controls (viewer button, disabled tooltip trigger).
 * Prefers an external caption, then the built-in label, and appends the displayed value.
 */
function getInteractableLabelledBy({
  label,
  labelId,
  labelledBy,
  valueId,
}: {
  label?: ReactNode;
  labelId: string;
  labelledBy?: string;
  valueId: string;
}): string | undefined {
  const captionId = labelledBy ?? (label != null ? labelId : undefined);
  if (captionId == null) {
    return undefined;
  }
  return `${captionId} ${valueId}`;
}

export interface MantineBaseEditableFieldClassNames {
  error?: string;
  label?: string;
  shell?: string;
}

export interface MantineBaseEditableFieldProps {
  /**
   * Class names for elements this component renders.
   * Style `editor` and `viewer` on the nodes you pass in.
   */
  classNames?: MantineBaseEditableFieldClassNames;
  dataTestAttrs?: DataTestAttrs;
  disabled?: boolean;
  disabledTooltip?: string | null;
  editor: ReactNode;
  editorId?: string;
  /** Shown only while editing. */
  error?: string | null;
  isEditing: boolean;
  isSaveEnabled: boolean;
  label?: ReactNode;
  /** Id of an existing caption used as the accessible name. */
  labelledBy?: string;
  onCancel: () => void;
  onSave: () => void;
  onStartEdit: (e: ElementMouseEvent) => void | Promise<void>;
  trailingSlot?: ReactNode;
  viewer: ReactNode;
}

function preventDisabledActivate(e: ElementMouseEvent): void {
  e.preventDefault();
}

interface FieldViewerButtonProps {
  ariaDisabled?: boolean;
  ariaLabelledBy?: string;
  onClick: (e: ElementMouseEvent) => void | Promise<void>;
  showEditIcon: boolean;
  testElement: 'disabledViewer' | 'viewer';
  tooltip?: string;
  valueId?: string;
  viewer: ReactNode;
}

function FieldViewerButton({
  ariaDisabled = false,
  ariaLabelledBy,
  onClick,
  showEditIcon,
  testElement,
  tooltip,
  valueId,
  viewer,
}: FieldViewerButtonProps): JSX.Element {
  const {classes, cx} = useStyles();
  const button = (
    <UnstyledButton
      aria-disabled={ariaDisabled || undefined}
      className={cx(classes.viewerSurface, showEditIcon && classes.viewerButton)}
      onClick={onClick}
      {...(ariaLabelledBy != null ? {'aria-labelledby': ariaLabelledBy} : {})}
      {...getTestProps({component: 'MantineBaseEditableField', element: testElement})}
    >
      <FieldViewerInner showEditIcon={showEditIcon} valueId={valueId} viewer={viewer} />
    </UnstyledButton>
  );
  if (tooltip == null) {
    return button;
  }
  return (
    <Tooltip events={{focus: true, hover: true, touch: false}} label={tooltip}>
      {button}
    </Tooltip>
  );
}

interface FieldViewerInnerProps {
  showEditIcon: boolean;
  valueId?: string;
  viewer: ReactNode;
}

function FieldViewerInner({showEditIcon, valueId, viewer}: FieldViewerInnerProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  return (
    <Flex align="center" gap="sm" h="100%" pr="sm" w="100%" wrap="nowrap">
      <Flex align="center" className={classes.main} id={valueId}>
        {viewer}
      </Flex>
      {showEditIcon && (
        <BenchlingIcon
          aria-hidden={true}
          className={classes.editIcon}
          icon="edit"
          {...getTestProps({component: 'MantineBaseEditableField', element: 'edit'})}
        />
      )}
    </Flex>
  );
}

interface EditModeActionsProps {
  isSaveEnabled: boolean;
  onCancel: () => void;
  onSave: () => void;
}

function EditModeActions({isSaveEnabled, onCancel, onSave}: EditModeActionsProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();

  return (
    <Group
      noWrap={true}
      spacing="xs"
      {...getTestProps({component: 'MantineBaseEditableField', element: 'actions'})}
    >
      <UnstyledButton
        aria-label="Cancel"
        className={cx(classes.actionIconButton, classes.cancelActionIcon)}
        onClick={onCancel}
        {...getTestProps({component: 'MantineBaseEditableField', element: 'cancel'})}
      >
        <BenchlingIcon icon="close" />
      </UnstyledButton>
      <UnstyledButton
        aria-label="Save"
        className={cx(classes.actionIconButton, classes.saveActionIcon)}
        disabled={!isSaveEnabled}
        onClick={onSave}
        {...getTestProps({component: 'MantineBaseEditableField', element: 'save'})}
      >
        <BenchlingIcon icon="check" />
      </UnstyledButton>
    </Group>
  );
}

export default function MantineBaseEditableField({
  classNames,
  dataTestAttrs,
  disabled = false,
  disabledTooltip,
  editor,
  editorId,
  error,
  isEditing,
  isSaveEnabled,
  label,
  labelledBy,
  onCancel,
  onSave,
  onStartEdit,
  trailingSlot,
  viewer,
}: MantineBaseEditableFieldProps): JSX.Element {
  const generatedId = useId();
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const hasError = hasFieldError(error);
  const idStem = editorId ?? generatedId;
  const labelId = `${idStem}-label`;
  const valueId = `${idStem}-value`;
  const interactableLabelledBy = getInteractableLabelledBy({label, labelId, labelledBy, valueId});

  const viewerValueId = interactableLabelledBy != null ? valueId : undefined;
  let content: ReactNode;
  if (disabled) {
    if (disabledTooltip != null) {
      content = (
        <FieldViewerButton
          ariaDisabled={true}
          ariaLabelledBy={interactableLabelledBy}
          onClick={preventDisabledActivate}
          showEditIcon={false}
          testElement="disabledViewer"
          tooltip={disabledTooltip}
          valueId={viewerValueId}
          viewer={viewer}
        />
      );
    } else {
      content = (
        <Flex
          align="center"
          className={classes.viewerSurface}
          {...getTestProps({component: 'MantineBaseEditableField', element: 'disabledViewer'})}
        >
          <FieldViewerInner showEditIcon={false} viewer={viewer} />
        </Flex>
      );
    }
  } else if (isEditing) {
    content = (
      <>
        <Flex align="stretch" className={classes.main}>
          {editor}
        </Flex>
        <Flex
          align="center"
          className={classes.trailingSlot}
          {...getTestProps({component: 'MantineBaseEditableField', element: 'trailingSlot'})}
        >
          {trailingSlot ?? (
            <EditModeActions isSaveEnabled={isSaveEnabled} onCancel={onCancel} onSave={onSave} />
          )}
        </Flex>
      </>
    );
  } else {
    content = (
      <FieldViewerButton
        ariaLabelledBy={interactableLabelledBy}
        onClick={onStartEdit}
        showEditIcon={true}
        testElement="viewer"
        valueId={viewerValueId}
        viewer={viewer}
      />
    );
  }

  return (
    <Stack spacing="xs" w="100%" {...getTestProps(dataTestAttrs)}>
      {label != null && (
        <Input.Label
          className={classNames?.label}
          htmlFor={isEditing && !disabled ? editorId : undefined}
          id={labelId}
          {...getTestProps({component: 'MantineBaseEditableField', element: 'label'})}
        >
          {label}
        </Input.Label>
      )}
      {/* Prefer Flex: Group applies the same flex-grow to every child. */}
      <Flex
        align="stretch"
        className={classNames?.shell}
        gap="xs"
        w="100%"
        wrap="nowrap"
        {...getTestProps({component: 'MantineBaseEditableField', element: 'shell'})}
      >
        {content}
      </Flex>
      {hasError && isEditing && !disabled && (
        <Input.Error
          className={classNames?.error}
          id={editorId != null ? `${editorId}-error` : undefined}
          {...getTestProps({component: 'MantineBaseEditableField', element: 'error'})}
        >
          {error}
        </Input.Error>
      )}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  actionIconButton: {
    alignItems: 'center',
    color: theme.colors.gray[6],
    display: 'flex',
    flexShrink: 0,
    height: ACTION_ICON_BUTTON_SIZE,
    justifyContent: 'center',
    width: ACTION_ICON_BUTTON_SIZE,
    '& svg': {
      height: ACTION_ICON_GLYPH_SIZE,
      width: ACTION_ICON_GLYPH_SIZE,
    },
    '&:disabled': {
      color: theme.colors.gray[4],
      cursor: 'default',
      pointerEvents: 'none',
    },
  },
  cancelActionIcon: {
    '&:hover:not(:disabled)': {
      color: theme.colors.red[6],
    },
  },
  editIcon: {
    ref: getStylesRef('editIcon'),
    color: theme.colors.gray[6],
    flexShrink: 0,
    height: ACTION_ICON_GLYPH_SIZE,
    visibility: 'hidden',
    width: ACTION_ICON_GLYPH_SIZE,
    '&:hover': {
      color: theme.colors.blue[6],
    },
  },
  main: {
    flex: 1,
    minWidth: 0,
  },
  saveActionIcon: {
    '&:hover:not(:disabled)': {
      color: theme.colors.blue[6],
    },
  },
  trailingSlot: {
    flexShrink: 0,
  },
  viewerButton: {
    [`&:hover .${getStylesRef('editIcon')}, &:focus-within .${getStylesRef('editIcon')}`]: {
      visibility: 'visible',
    },
  },
  viewerSurface: {
    borderRadius: theme.radius.sm,
    flex: 1,
    minWidth: 0,
    '&:hover, &:focus-within': {
      backgroundColor: theme.colors.gray[0],
    },
  },
}));
